import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { CommandResult } from './interfaces';
import { TranscriptEditorChangeType } from '../types';
import { TranscriptElement } from '../models/TranscriptElement';

/**
 * Command for updating elements in a paragraph
 * 
 * This command handles:
 * 1. Replacing all elements in a paragraph with new ones
 * 2. Updating paragraph timing based on the new elements
 * 3. Storing the old elements for undo operations
 */
export class UpdateParagraphElementsCommand extends AbstractTranscriptEditorCommand {
  private segmentId: string;
  private paragraphId: string;
  private newElements: TranscriptElement[];
  private oldElements: TranscriptElement[] = [];
  private userId: string;

  /**
   * Create a new UpdateParagraphElementsCommand
   * 
   * @param editor Reference to the transcript editor core
   * @param segmentId ID of the segment containing the paragraph
   * @param paragraphId ID of the paragraph to update
   * @param newElements New elements to replace the existing ones
   * @param userId ID of the user performing the operation
   */
  constructor(
    editor: TranscriptEditorCore,
    segmentId: string,
    paragraphId: string,
    newElements: TranscriptElement[],
    userId: string
  ) {
    super(`Update elements for paragraph ${paragraphId}`, editor);
    this.segmentId = segmentId;
    this.paragraphId = paragraphId;
    this.newElements = newElements;
    this.userId = userId;
    
    // Set metadata for debugging/tracking
    this.setMetadata('operation', 'update_paragraph_elements');
    this.setMetadata('segmentId', segmentId);
    this.setMetadata('paragraphId', paragraphId);
    this.setMetadata('userId', userId);
    this.setMetadata('elementsCount', newElements.length);
  }

  /**
   * Execute the command to update paragraph elements
   * 
   * @returns True if successful, false otherwise
   */
  async execute(): Promise<boolean> {
    try {
      if (!this.editor.getContainer()) return false;
      
      const segment = this.editor.getContainer()?.getSegment(this.segmentId);
      if (!segment) return false;
      
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) return false;
      
      // Store old elements for potential undo
      this.oldElements = [...paragraph.elements];
      
      // Update the paragraph elements
      paragraph.elements = this.newElements;
      
      // Update paragraph timing based on elements
      this.updateParagraphTiming(paragraph);
      
      // Record the operation
      this.editor.recordOperation({
        type: 'paragraph_elements_update',
        segmentId: this.segmentId,
        paragraphId: this.paragraphId,
        data: {
          oldElements: this.oldElements,
          newElements: this.newElements
        },
        userId: this.userId,
        timestamp: Date.now()
      });
      
      // Notify observers of the change
      this.editor.notifyObservers(TranscriptEditorChangeType.PARAGRAPH_ELEMENTS_UPDATED, {
        segment,
        paragraph,
        oldElements: this.oldElements,
        newElements: this.newElements
      });
      
      return true;
    } catch (error) {
      console.error(`Error in UpdateParagraphElementsCommand.execute:`, error);
      return false;
    }
  }

  /**
   * Undo the paragraph elements update
   * 
   * @returns True if successful, false otherwise
   */
  async undo(): Promise<boolean> {
    try {
      if (!this.editor.getContainer()) return false;
      
      const segment = this.editor.getContainer()?.getSegment(this.segmentId);
      if (!segment) return false;
      
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) return false;
      
      // Restore the old elements
      paragraph.elements = this.oldElements;
      
      // Update paragraph timing based on restored elements
      this.updateParagraphTiming(paragraph);
      
      // Notify observers of the undo
      this.editor.notifyObservers(TranscriptEditorChangeType.PARAGRAPH_ELEMENTS_UPDATED, {
        segment,
        paragraph,
        oldElements: this.newElements,
        newElements: this.oldElements
      });
      
      return true;
    } catch (error) {
      console.error(`Error in UpdateParagraphElementsCommand.undo:`, error);
      return false;
    }
  }
  
  /**
   * Update paragraph timing based on its elements
   * 
   * @param paragraph The paragraph to update timing for
   */
  private updateParagraphTiming(paragraph: any): void {
    const textElements = paragraph.elements.filter(
      (el: any) => el.type === 'text'
    );
    
    if (textElements.length === 0) return;
    
    // Find start time (first element with timing)
    const firstElement = textElements.find((el: any) => el.startTime !== undefined);
    if (firstElement && firstElement.startTime !== undefined) {
      paragraph.startTime = firstElement.startTime;
    }
    
    // Find end time (last element with timing)
    const lastElement = [...textElements].reverse().find((el: any) => el.endTime !== undefined);
    if (lastElement && lastElement.endTime !== undefined) {
      paragraph.endTime = lastElement.endTime;
    }
  }
}